import { dateProvider } from '@utils';
import { Select } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DateWrapper,
  ItemTextWrapper,
  ItemWrapper,
  OptionsWrapper
} from './ExamGroupSelectorPCE.styles';

import { Icon } from '@/components/Icon';
import { useExamGroupOptionsPCE } from '@/store';

type Props = {
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  placeholder?: string;
  styles?: React.CSSProperties;
  mode?: 'multiple' | 'tags';
  suffixIcon?: React.ReactNode;
  onExamGroupSelect?: (date: Date | undefined) => void;
};

export const ExamGroupSelectorPCE: React.FC<Props> = ({
  value,
  onChange,
  placeholder,
  styles,
  mode,
  suffixIcon,
  onExamGroupSelect
}) => {
  const { t } = useTranslation();
  const { data: examGroups } = useExamGroupOptionsPCE();
  const [searchText, setSearchText] = useState('');
  const options = examGroups?.map(c => ({
    label: c.name,
    value: c._id,
    examDate: c.examDate
  }));
  const filteredOptions = searchText
    ? options?.filter(opt => opt.label.toLowerCase().includes(searchText.toLowerCase()))
    : options;
  const handleChange = (val: string | string[], date: Date | undefined) => {
    if (Array.isArray(val)) {
      onChange?.(val);
    } else {
      onChange?.([val, ...(value ?? [])]);
    }
    onExamGroupSelect?.(date);
  };

  return (
    <Select
      mode={mode}
      showSearch
      style={styles}
      options={filteredOptions}
      onSearch={setSearchText}
      onChange={val => {
        if (!val) {
          handleChange(val, undefined);
          setSearchText('');
          return;
        }
        const selectedOption = options?.find(opt => opt.value === val);
        handleChange(val, selectedOption?.examDate);
        setSearchText('');
      }}
      value={value}
      filterOption={(input, option) =>
        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
      }
      placeholder={placeholder ?? t('SELECT_COURSE')}
      suffixIcon={suffixIcon}
      dropdownRender={() => {
        return (
          <OptionsWrapper>
            {filteredOptions?.map(({ label, value: val, examDate }) => {
              const isSelected =
                value && Array.isArray(value) ? value.includes(val) : value === val;
              return (
                <ItemWrapper
                  key={val}
                  $isSelected={isSelected}
                  onClick={() => handleChange(val, examDate)}>
                  <ItemTextWrapper>{label}</ItemTextWrapper>
                  <DateWrapper>
                    {examDate ? dateProvider.format(examDate, '(dd/MM/yyyy)') : undefined}
                  </DateWrapper>
                  {isSelected && <Icon i="Checkmark" color="green7" />}
                </ItemWrapper>
              );
            })}
          </OptionsWrapper>
        );
      }}
    />
  );
};
